// Centralized validation utilities for projects, pages, objectives, and components

// Component-to-page compatibility rules
export const COMPONENT_RULES = {
  "Splash Page": {
    allowedPageTypes: ["Project Splash Page (Preapply)"],
    message:
      "Splash Page components can only be added to 'Project Splash Page (Preapply)'.",
  },
};

export function isComponentAllowedForPage(
  componentType,
  pageType,
  rules = COMPONENT_RULES,
) {
  const rule = rules[componentType];
  if (!rule) return true;
  const { allowedPageTypes } = rule;
  if (Array.isArray(allowedPageTypes) && allowedPageTypes.length > 0) {
    return allowedPageTypes.includes(pageType);
  }
  return true;
}

export function getComponentRuleMessage(
  componentType,
  rules = COMPONENT_RULES,
) {
  return rules[componentType]?.message || "";
}

// ---- Page level helpers ----

function coalesce(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function normalizeId(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object" && value.id != null) return String(value.id);
  return null;
}

// Attempts to infer a page relation. If no objective id is present, defaults to project-level.
export function getPageRelation(page) {
  const explicitScope = coalesce(page?.scope, page?.level, page?.targetLevel);
  const recognizedObjectiveId = coalesce(
    page?.objectiveId,
    page?.objective_id,
    page?.relatedObjectiveId,
    page?.objective?.id,
  );

  const normalizedObjectiveId = normalizeId(recognizedObjectiveId);

  if (explicitScope && normalizedObjectiveId) {
    if (explicitScope === "project") {
      return {
        scope: "invalid",
        objectiveId: normalizedObjectiveId,
        reason: "PAGE_HAS_PROJECT_SCOPE_AND_OBJECTIVE",
      };
    }
    if (explicitScope === "objective") {
      return { scope: "objective", objectiveId: normalizedObjectiveId };
    }
  }

  if (normalizedObjectiveId) {
    return { scope: "objective", objectiveId: normalizedObjectiveId };
  }

  // Default to project-level when no objective is referenced
  return { scope: "project", objectiveId: null };
}

// ---- Objective rules ----

function normalizeString(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function getObjectiveLangCountry(objective) {
  if (!objective || typeof objective !== "object")
    return { language: "", country: "" };

  const language = coalesce(
    objective.language,
    objective.languageCode,
    objective.lang,
    objective?.locale?.language,
  );

  const country = coalesce(
    objective.country,
    objective.countryCode,
    objective.locale,
    objective?.locale?.country,
  );

  return {
    language: normalizeString(language),
    country: normalizeString(country),
  };
}

export function validateObjectives(objectives) {
  const errors = [];

  // Handle single objectives object (from form)
  if (!Array.isArray(objectives)) {
    if (!objectives || typeof objectives !== "object") {
      return errors;
    }

    // Validate mandatory fields for single objectives object
    if (!objectives.project || objectives.project.trim() === "") {
      errors.push({
        field: "project",
        message: "Project is required",
      });
    }

    if (!objectives.workType || objectives.workType.trim() === "") {
      errors.push({
        field: "workType",
        message: "Work Type is required",
      });
    }

    if (
      !objectives.productivityTargetType ||
      objectives.productivityTargetType.trim() === ""
    ) {
      errors.push({
        field: "productivityTargetType",
        message: "Productivity Target Type is required",
      });
    }

    if (
      !objectives.daysBetweenReminderEmails ||
      objectives.daysBetweenReminderEmails === ""
    ) {
      errors.push({
        field: "daysBetweenReminderEmails",
        message: "Days Between Reminder Emails is required",
      });
    }

    return errors;
  }

  // Handle array of objectives (legacy support)
  const seen = new Map();
  objectives.forEach((obj, index) => {
    const { language, country } = getObjectiveLangCountry(obj);
    if (!language && !country) return; // skip if not provided yet
    const key = `${language}::${country}`;
    if (seen.has(key)) {
      errors.push({
        code: "OBJECTIVE_LANGUAGE_COUNTRY_DUPLICATE",
        message: "Multiple objectives share the same language and country.",
        path: `objectives[${index}]`,
        meta: { duplicateOf: seen.get(key), language, country },
      });
    } else {
      seen.set(key, index);
    }
  });

  return errors;
}

// ---- Page rules ----

export function validatePages(
  pages = [],
  { componentRules = COMPONENT_RULES } = {},
) {
  const errors = [];
  if (!Array.isArray(pages) || pages.length === 0) return errors;

  // Rule: Only one active project page allowed per project (global across scopes)
  const activePages = pages.filter((p) => p?.type === "Project Active Page");
  if (activePages.length > 1) {
    errors.push({
      code: "MULTIPLE_ACTIVE_PAGES",
      message: "Only one 'Project Active Page' is allowed per project.",
      path: "pages",
      meta: {
        count: activePages.length,
        pageIds: activePages.map((p) => p.id),
      },
    });
  }

  // Count pages per type at project level
  const projectLevelTypeCounts = new Map(); // type => count
  // Count pages per type per objective
  const objectiveIdToTypeCounts = new Map(); // objectiveId => (type => count)

  pages.forEach((page, index) => {
    const rel = getPageRelation(page);
    if (rel.scope === "invalid") {
      errors.push({
        code: "PAGE_RELATION_INVALID",
        message:
          "A page cannot be marked as project-level while referencing an objective.",
        path: `pages[${index}]`,
        meta: { pageId: page?.id, objectiveId: rel.objectiveId },
      });
      return;
    }

    const pageType = page?.type || "";
    if (!pageType) return;

    if (rel.scope === "project") {
      const current = projectLevelTypeCounts.get(pageType) || 0;
      projectLevelTypeCounts.set(pageType, current + 1);
    } else if (rel.scope === "objective") {
      const typeCounts =
        objectiveIdToTypeCounts.get(rel.objectiveId) || new Map();
      const current = typeCounts.get(pageType) || 0;
      typeCounts.set(pageType, current + 1);
      objectiveIdToTypeCounts.set(rel.objectiveId, typeCounts);
    }

    // Component-level compatibility checks
    if (Array.isArray(page?.components)) {
      page.components.forEach((c, compIndex) => {
        if (!isComponentAllowedForPage(c?.type, page?.type, componentRules)) {
          errors.push({
            code: "COMPONENT_NOT_ALLOWED_ON_PAGE",
            message: `${c?.type || "Component"} cannot be added to ${
              page?.type || "this page"
            }.`,
            path: `pages[${index}].components[${compIndex}]`,
            meta: { pageId: page?.id, componentId: c?.id },
          });
        }
      });
    }
  });

  // New Rule: Only one page of each type at the project level
  for (const [type, count] of projectLevelTypeCounts.entries()) {
    if (count > 1) {
      errors.push({
        code: "DUPLICATE_PAGE_TYPE_AT_PROJECT_LEVEL",
        message: `Only one project-level page of type "${type}" is allowed.`,
        path: "pages",
        meta: { type, count },
      });
    }
  }

  // New Rule: Only one page of each type per objective
  for (const [objectiveId, typeCounts] of objectiveIdToTypeCounts.entries()) {
    for (const [type, count] of typeCounts.entries()) {
      if (count > 1) {
        errors.push({
          code: "DUPLICATE_PAGE_TYPE_AT_OBJECTIVE_LEVEL",
          message: `Only one page of type "${type}" is allowed for a single objective.`,
          path: "pages",
          meta: { objectiveId, type, count },
        });
      }
    }
  }

  return errors;
}

// ---- Project level ----

export function validateProject(project) {
  const errors = [];
  if (!project || typeof project !== "object") return errors;

  // Objectives may be an object or an array depending on the UI/data source
  const objectivesCandidate =
    project.objectives ?? project.objectiveList ?? project.objective ?? [];
  const objectiveErrors = validateObjectives(objectivesCandidate);
  if (objectiveErrors.length > 0) errors.push(...objectiveErrors);

  const pageErrors = validatePages(project.pages || []);
  if (pageErrors.length > 0) errors.push(...pageErrors);

  return errors;
}

// Convenience guards for create flows
export function canAddPage(existingProject, pageToAdd) {
  const simulated = Array.isArray(existingProject?.pages)
    ? [...existingProject.pages, pageToAdd]
    : [pageToAdd];
  const errors = validatePages(simulated);
  return { ok: errors.length === 0, errors };
}

export function canAddComponent(existingProject, pageId, componentToAdd) {
  const pages = Array.isArray(existingProject?.pages)
    ? existingProject.pages
    : [];
  const simulated = pages.map((p) => {
    if (p?.id === pageId) {
      const components = Array.isArray(p.components)
        ? [...p.components, componentToAdd]
        : [componentToAdd];
      return { ...p, components };
    }
    return p;
  });
  const errors = validatePages(simulated);
  return { ok: errors.length === 0, errors };
}
